import React, { type ReactNode } from 'react';
import { motion } from 'framer-motion';

const gradients = [
  {
    colors: [
      'rgba(112, 172, 255, 1)',
      'rgba(179, 217, 255, 0.6)',
      'rgba(255, 255, 255, 0)',
    ],
    initial: { top: '-60%', left: '0%' },
    duration: 10,
    opacity: 1,
  },
  {
    colors: [
      'rgba(223, 191, 255, 0.9)',
      'rgba(245, 230, 255, 0.5)',
      'rgba(255, 255, 255, 0)',
    ],
    initial: { top: '-200%', left: '20%' },
    duration: 8,
    opacity: 1,
  },
  {
    colors: [
      'rgba(119, 13, 252, 1)',
      'rgba(179, 128, 255, 0.6)',
      'rgba(255, 255, 255, 0)',
    ],
    initial: { top: '0%', left: '50%' },
    duration: 9,
    opacity: 1,
  },
  {
    colors: [
      'rgba(248, 48, 185, 1)',
      'rgba(255, 179, 248, 0.6)',
      'rgba(255, 255, 255, 0)',
    ],
    initial: { top: '10%', left: '70%' },
    duration: 7,
    opacity: 1,
  },
  {
    colors: [
      'rgba(112, 172, 255, 1)',
      'rgba(179, 217, 255, 0.6)',
      'rgba(255, 255, 255, 0)',
    ],
    initial: { top: '40%', left: '10%' },
    duration: 8,
    opacity: 1,
  },
  {
    colors: [
      'rgba(223, 191, 255, 0.9)',
      'rgba(245, 230, 255, 0.5)',
      'rgba(255, 255, 255, 0)',
    ],
    initial: { top: '60%', left: '30%' },
    duration: 9,
    opacity: 1,
  },
  {
    colors: [
      'rgba(119, 13, 252, 1)',
      'rgba(200, 159, 248, 0.5)',
      'rgba(255, 255, 255, 0)',
    ],
    initial: { top: '80%', left: '50%' },
    duration: 8,
    opacity: 0.4,
  },
  {
    colors: [
      'rgba(248, 48, 185, 1)',
      'rgba(223, 128, 252, 0.5)',
      'rgba(255, 255, 255, 0)',
    ],
    initial: { top: '-80%', left: '-100%' },
    duration: 7,
    opacity: 0.3,
  },
];

const adjustSaturation = (color: string, factor: number) => {
  const [r, g, b, a] = color.match(/\d+\.?\d*/g)?.map(Number) || [];
  const adjust = (value: number) =>
    Math.min(255, Math.max(0, value + (255 - value) * (factor - 1)));
  return `rgba(${adjust(r)}, ${adjust(g)}, ${adjust(b)}, ${a ?? 1})`;
};

const AnimatedBackground: React.FC<{
  children: ReactNode;
  blurAmount?: number;
  opacityFactor?: number;
  saturationFactor?: number;
  className?: string;
}> = ({
  children,
  blurAmount = 4,
  opacityFactor = 1,
  saturationFactor = 1,
  className,
}) => {
  return (
    <div className={`flex overflow-hidden ${className}`}>
      <div className="relative w-full">
        {gradients.map((gradient, index) => (
          <motion.div
            key={index}
            className="absolute"
            style={{
              background: `radial-gradient(circle, ${gradient.colors
                .map((color) => adjustSaturation(color, saturationFactor))
                .join(', ')})`,
              filter: `blur(${blurAmount}vw)`,
              opacity: gradient.opacity * opacityFactor,
              width: '200%',
              height: '200%',
            }}
            initial={{ ...gradient.initial, scale: 1 }}
            animate={{
              x: ['-10%', '20%', '-20%', '10%', '-10%'],
              y: ['-10%', '-20%', '30%', '20%', '-10%'],
              scale: [1, 1.2, 1.3, 1.2, 1],
            }}
            transition={{
              duration: gradient.duration,
              ease: 'easeInOut',
              repeat: Infinity,
              repeatType: 'mirror',
            }}
          />
        ))}
        <div className="relative">{children}</div>
      </div>
    </div>
  );
};

export default AnimatedBackground;
